import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Package, CheckCircle, XCircle, Truck, Clock, LogOut, User, Power, Phone, ChevronRight, Menu, X, Wallet, Navigation, History, MessageCircle, CalendarCheck, AlertTriangle, BookOpen, UserCog } from 'lucide-react';
import Reglement from '../Reglement';
import { SkeletonRow } from '../../components/Skeleton';
import { APP_ROUTES } from '../../utils/auth';
import { getStoredProfiles, setStoredProfile } from '../../utils/session';
import { DELIVERY_STATUS_THEME, orderBarColor } from '../../constants/deliveryStatus';
import { buildDriverDailySummary, findParticulierProfile, groupDriverHistoryByDay, sortActiveDriverDeliveries } from './driverUtils';
import { useDriverRuntime } from './useDriverRuntime';
import { supabase } from '../../supabase';
import { AVATAR_BG, AVATAR_FG } from '../../constants/avatar';

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

/** Ancienneté lisible d'une position : "à l'instant", "il y a 4 min", "il y a 3 h" */
function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return '';
  const min = Math.floor(diff / 60000);
  if (min < 1)  return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)   return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return `il y a ${j} j`;
}

/** Au-delà de 2 h, la position peut ne plus refléter où se trouve le client */
function isStale(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > 2 * 60 * 60 * 1000;
}
const WORK_MINUTES = 540; // 9h
function workingDaysInMonth(year, month) {
  // 7j/7 dimanche inclus
  return new Date(year, month + 1, 0).getDate();
}

const statusConfig = {
  pending:   { ...DELIVERY_STATUS_THEME.pending, icon: Clock },
  transit:   { ...DELIVERY_STATUS_THEME.transit, icon: Truck },
  delivered: { ...DELIVERY_STATUS_THEME.delivered, icon: CheckCircle },
  failed:    { ...DELIVERY_STATUS_THEME.failed, icon: XCircle },
};

const nextStatus = {
  pending:   [
    { value: 'delivered', label: 'Marquer livré',    color: '#15803d', bg: 'linear-gradient(135deg, #22c55e, #15803d)' },
    { value: 'failed',    label: 'Retour livraison', color: '#be123c', bg: 'linear-gradient(135deg, #f43f5e, #be123c)' },
  ],
  transit:   [
    { value: 'delivered', label: 'Marquer livré',    color: '#15803d', bg: 'linear-gradient(135deg, #22c55e, #15803d)' },
    { value: 'failed',    label: 'Retour livraison', color: '#be123c', bg: 'linear-gradient(135deg, #f43f5e, #be123c)' },
  ],
  delivered: [
    { value: 'failed',    label: 'Marquer retour',   color: '#be123c', bg: 'linear-gradient(135deg, #f43f5e, #be123c)' },
  ],
  failed:    [
    { value: 'delivered', label: 'Marquer livré', color: '#15803d', bg: 'linear-gradient(135deg, #22c55e, #15803d)' },
  ],
};

export default function DriverApp() {
  const navigate = useNavigate();
  const {
    driver,
    deliveries,
    loading,
    isOnDuty,
    isProfileBased,
    gpsStatus,
    wakeLockActive,
    startDuty,
    endDuty,
    refreshGps,
    updateStatus,
    handleLogout,
  } = useDriverRuntime();
  const [activeTab, setActiveTab] = useState('active');
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showHistorique, setShowHistorique] = useState(false);
  const [showPresences, setShowPresences] = useState(false);
  const [showProfil, setShowProfil]       = useState(false);
  const [showReglement, setShowReglement] = useState(false);
  const [pinCurrent, setPinCurrent]       = useState('');
  const [pinNew, setPinNew]               = useState('');
  const [pinConfirm, setPinConfirm]       = useState('');
  const [pinSaving, setPinSaving]         = useState(false);
  const [pinError, setPinError]           = useState('');
  const [pinSuccess, setPinSuccess]       = useState(false);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [motifModal, setMotifModal] = useState(null); // { deliveryId, label }
  const [motifText, setMotifText] = useState('');
  const [gestionnaireProfile, setGestionnaireProfile] = useState(null);

  // Vérifier si ce livreur a aussi un profil gestionnaire (même numéro)
  useEffect(() => {
    if (!driver?.id) return;
    (async () => {
      const { data: driverProf } = await supabase.from('profiles').select('phone').eq('driver_id', driver.id).eq('role', 'driver').maybeSingle();
      if (!driverProf?.phone) return;
      const { data: gestProf } = await supabase.from('profiles').select('*').eq('phone', driverProf.phone).eq('role', 'gestionnaire').maybeSingle();
      if (gestProf) setGestionnaireProfile(gestProf);
    })();
  }, [driver?.id]);

  // Changement de PIN
  const handleChangePin = useCallback(async () => {
    setPinError('');
    if (pinNew.length < 4) { setPinError('Le nouveau PIN doit faire au moins 4 caractères.'); return; }
    if (pinNew !== pinConfirm) { setPinError('Les deux nouveaux PIN ne correspondent pas.'); return; }
    if (!driver?.id) return;
    setPinSaving(true);
    // Vérifier le PIN actuel
    const { default: hashPin } = await import('../../utils/hashPin');
    const currentHash = await hashPin(pinCurrent);
    const { data: prof } = await supabase.from('profiles').select('pin_hash').eq('driver_id', driver.id).maybeSingle();
    if (prof?.pin_hash && prof.pin_hash !== currentHash) {
      setPinError('PIN actuel incorrect.'); setPinSaving(false); return;
    }
    const newHash = await hashPin(pinNew);
    await supabase.from('profiles').update({ pin_hash: newHash }).eq('driver_id', driver.id);
    setPinCurrent(''); setPinNew(''); setPinConfirm('');
    setPinSaving(false); setPinSuccess(true);
    setTimeout(() => setPinSuccess(false), 3000);
  }, [driver, pinCurrent, pinNew, pinConfirm]);

  // Fetch attendance du mois en cours
  const fetchAttendance = useCallback(async () => {
    if (!driver?.id) return;
    setAttendanceLoading(true);
    const now = new Date();
    const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay  = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const { data } = await supabase
      .from('attendance_logs')
      .select('*')
      .eq('driver_id', driver.id)
      .gte('date', firstDay)
      .lte('date', lastDay)
      .order('date', { ascending: false });
    if (data) setAttendanceLogs(data);
    setAttendanceLoading(false);
  }, [driver?.id]);

  useEffect(() => {
    if (showPresences) fetchAttendance();
  }, [showPresences, fetchAttendance]);
  
  useEffect(() => {
    if (!selectedDelivery) return;
    const freshDelivery = deliveries.find((delivery) => delivery.id === selectedDelivery.id);
    if (!freshDelivery) {
      setSelectedDelivery(null);
      return;
    }
    if (freshDelivery !== selectedDelivery) setSelectedDelivery(freshDelivery);
  }, [deliveries, selectedDelivery]);

  useEffect(() => {
    if (!selectedDelivery) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [selectedDelivery]);

  const {
    active,
    totalToCollect,
    todayDelivered,
    todayRetours,
    todayAmount,
    historiqueItems,
  } = useMemo(
    () => buildDriverDailySummary(deliveries),
    [deliveries]
  );

  const sortedActive = useMemo(() => sortActiveDriverDeliveries(active), [active]);

  const activeList = activeTab === 'active' ? sortedActive : activeTab === 'retours' ? todayRetours : [];

  const historiqueByDay = useMemo(() => groupDriverHistoryByDay(historiqueItems), [historiqueItems]);

  const handleStatusUpdate = async (deliveryId, newStatus, label, returnReason) => {
    const didUpdate = await updateStatus(deliveryId, newStatus, label, returnReason);
    if (didUpdate) setSelectedDelivery(null);
  };

  const handleActionClick = (deliveryId, action) => {
    if (action.value === 'failed') {
      setMotifText('');
      setMotifModal({ deliveryId, label: action.label });
    } else {
      handleStatusUpdate(deliveryId, action.value, action.label);
    }
  };

  const handleMotifConfirm = async () => {
    if (!motifModal) return;
    await handleStatusUpdate(motifModal.deliveryId, 'failed', motifModal.label, motifText.trim());
    setMotifModal(null);
    setMotifText('');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f3f8', paddingBottom: '24px' }}>

      {/* ── Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
        {/* Avatar + nom + statut */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontWeight: 800, fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
              {driver?.initials || '?'}
            </div>
            <div style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '11px', height: '11px', borderRadius: '50%', background: isOnDuty ? '#22c55e' : '#64748b', border: '2px solid #07162d' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '15px', fontWeight: 800, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {driver?.name || 'Livreur'}
            </div>
            <div style={{ fontSize: '11px', color: isOnDuty ? '#4ade80' : 'rgba(255,255,255,0.35)', fontWeight: 600 }}>
              {isOnDuty ? '● En service' : '● Hors service'}
            </div>
          </div>
        </div>
        <button onClick={() => setShowMenu(true)}
          style={{ background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: '9px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}
          aria-label="Menu">
          <Menu size={16} />
        </button>
      </div>

      {/* Side menu */}
      {showMenu && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowMenu(false)}
          />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px', zIndex: 50, background: 'linear-gradient(180deg, #07162d 0%, #112c52 100%)', display: 'flex', flexDirection: 'column', boxShadow: '12px 0 40px rgba(0,0,0,0.4)' }}>
            <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontWeight: 800, fontSize: '15px', flexShrink: 0 }}>
                {driver?.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{driver?.name || 'Livreur'}</div>
                <div style={{ fontSize: '11px', color: isOnDuty ? '#4ade80' : 'rgba(255,255,255,0.4)', marginTop: '1px' }}>
                  {isOnDuty ? 'En service' : 'Hors service'}
                </div>
              </div>
              <button
                onClick={() => setShowMenu(false)}
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: 'white', padding: '7px', borderRadius: '8px', display: 'flex', flexShrink: 0 }}
                aria-label="Fermer le menu"
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
              <button
                onClick={() => { setShowHistorique(true); setShowMenu(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(23,59,120,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <History size={15} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Mon historique</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Livraisons des jours précédents</div>
                </div>
              </button>
              <button
                onClick={() => { setShowProfil(true); setShowMenu(false); setPinError(''); setPinSuccess(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(109,40,217,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={15} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Mon profil</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Informations et sécurité</div>
                </div>
              </button>
              <button
                onClick={() => { setShowReglement(true); setShowMenu(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(184,50,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOpen size={15} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Règlement intérieur</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Règles et engagements</div>
                </div>
              </button>
              <button
                onClick={() => { setShowPresences(true); setShowMenu(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(21,128,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CalendarCheck size={15} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Ma présence</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Pointage et assiduité du mois</div>
                </div>
              </button>
              <button
                onClick={() => { if (isOnDuty) endDuty(); else startDuty(); setShowMenu(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: isOnDuty ? 'rgba(185,28,28,0.45)' : 'rgba(21,128,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Power size={15} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{isOnDuty ? 'Terminer le service' : 'Démarrer le service'}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                    {isOnDuty ? 'Passer hors ligne' : 'Activer GPS et disponibilité'}
                  </div>
                </div>
              </button>
              {gestionnaireProfile && (
                <button
                  onClick={() => {
                    setStoredProfile(gestionnaireProfile);
                    setShowMenu(false);
                    navigate(APP_ROUTES.gestionnaire);
                  }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(109,40,217,0.4)', background: 'rgba(109,40,217,0.15)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
                >
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(109,40,217,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <UserCog size={15} color="#c4b5fd" />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Mode Gestionnaire</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Basculer vers la gestion</div>
                  </div>
                </button>
              )}
              {isProfileBased && (
                <button
                  onClick={() => {
                    const particulier = findParticulierProfile(getStoredProfiles());
                    if (particulier) setStoredProfile(particulier);
                    setShowMenu(false);
                    navigate(APP_ROUTES.particulier);
                  }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.35)', background: 'rgba(34,197,94,0.12)', cursor: 'pointer', textAlign: 'left', marginBottom: '6px' }}
                >
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(22,163,74,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <User size={15} color="#86efac" />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>Basculer Particulier</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Changer de rôle</div>
                  </div>
                </button>
              )}
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={handleLogout}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', border: '1px solid rgba(197,58,45,0.4)', background: 'rgba(197,58,45,0.1)', cursor: 'pointer', color: '#f87171', fontSize: '13px', fontWeight: 600 }}
              >
                <LogOut size={15} /> Se déconnecter
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ padding: '12px 12px 0' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>

          {/* ── Hero card : service + stats ── */}
          <div style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '12px', boxShadow: '0 8px 28px rgba(10,22,40,0.18)' }}>
            <div style={{ background: 'linear-gradient(145deg, #07162d 0%, #112c52 100%)', padding: '16px 18px' }}>

              {/* Ligne service status + action */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isOnDuty ? '12px' : '0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isOnDuty ? '#22c55e' : '#64748b', boxShadow: isOnDuty ? '0 0 10px #22c55e' : 'none', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: isOnDuty ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>
                      {isOnDuty ? 'En service' : 'Hors service'}
                    </div>
                    {!isOnDuty && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '1px' }}>Active le GPS pour recevoir des livraisons</div>}
                  </div>
                </div>
                {isOnDuty ? (
                  <button onClick={endDuty}
                    style={{ padding: '7px 13px', borderRadius: '9px', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    <Power size={12} /> Terminer
                  </button>
                ) : (
                  <button onClick={startDuty}
                    style={{ padding: '10px 18px', borderRadius: '11px', border: 'none', background: 'linear-gradient(135deg, #22c55e, #15803d)', color: 'white', fontSize: '13px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', boxShadow: '0 4px 16px rgba(34,197,94,0.4)', flexShrink: 0 }}>
                    <Power size={14} /> Démarrer
                  </button>
                )}
              </div>

              {/* Indicateur GPS */}
              {isOnDuty && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '5px 10px', borderRadius: '8px',
                      background: gpsStatus === 'error' ? 'rgba(239,68,68,0.15)' : gpsStatus === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${gpsStatus === 'error' ? 'rgba(239,68,68,0.35)' : gpsStatus === 'ok' ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      flex: 1,
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                        background: gpsStatus === 'error' ? '#f87171' : gpsStatus === 'ok' ? '#4ade80' : '#94a3b8',
                        boxShadow: gpsStatus === 'ok' ? '0 0 6px #4ade80' : 'none',
                        animation: gpsStatus === 'ok' ? 'pulse 2s infinite' : 'none',
                      }} />
                      <span style={{ fontSize: '11px', fontWeight: 600, color: gpsStatus === 'error' ? '#f87171' : gpsStatus === 'ok' ? '#4ade80' : 'rgba(255,255,255,0.4)' }}>
                        {gpsStatus === 'error' ? 'Signal GPS non envoyé' : gpsStatus === 'ok' ? 'Position GPS transmise' : 'GPS en attente...'}
                      </span>
                    </div>
                    <button onClick={refreshGps} title="Actualiser la position GPS"
                      style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      Rafraîchir
                    </button>
                  </div>
                  {/* Wake lock — écran maintenu actif */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 8px', borderRadius: '7px',
                    background: wakeLockActive ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${wakeLockActive ? 'rgba(251,191,36,0.25)' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                    <span style={{ fontSize: '12px' }}>{wakeLockActive ? '🔆' : '🔅'}</span>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: wakeLockActive ? '#fbbf24' : 'rgba(255,255,255,0.25)' }}>
                      {wakeLockActive ? 'Écran maintenu actif — GPS continu' : 'Écran libre (GPS peut s\'interrompre)'}
                    </span>
                  </div>
                </div>
              )}

              {/* Stats tâches */}
              {isOnDuty && (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, marginBottom: '3px' }}>Collecté total</div>
                      <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '28px', fontWeight: 800, color: 'white', lineHeight: 1 }}>
                        {todayAmount.toLocaleString('fr-FR')}
                        <span style={{ fontSize: '13px', opacity: 0.4, fontWeight: 600, marginLeft: '4px' }}>FCFA</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[
                        { value: todayDelivered.length, label: 'Livrées', color: '#4ade80',  bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.2)' },
                        { value: active.length,         label: 'En cours', color: '#93c5fd',  bg: 'rgba(147,197,253,0.12)', border: 'rgba(147,197,253,0.2)' },
                        { value: todayRetours.length,   label: 'Retours',  color: '#fca5a5',  bg: 'rgba(252,165,165,0.12)', border: 'rgba(252,165,165,0.2)' },
                      ].map((s, i) => (
                        <div key={i} style={{ textAlign: 'center', padding: '7px 8px', borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}`, width: '60px', flexShrink: 0 }}>
                          <div style={{ fontSize: '20px', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)', fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Barre de progression : journée en cours */}
                  {(active.length + todayDelivered.length + todayRetours.length) > 0 && (() => {
                    const total = active.length + todayDelivered.length + todayRetours.length;
                    const done  = todayDelivered.length + todayRetours.length;
                    const pct   = Math.round((done / total) * 100);
                    return (
                      <div style={{ marginTop: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>Progression du jour</span>
                          <span style={{ fontSize: '10px', fontWeight: 800, color: pct === 100 ? '#4ade80' : 'rgba(255,255,255,0.6)' }}>{done}/{total} · {pct}%</span>
                        </div>
                        <div style={{ height: '5px', borderRadius: '999px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '999px', background: pct === 100 ? 'linear-gradient(90deg,#4ade80,#15803d)' : 'linear-gradient(90deg,#60a5fa,#3b82f6)', width: `${pct}%`, transition: 'width 0.4s ease' }} />
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Bande "À collecter" */}
            {totalToCollect > 0 && (
              <div style={{ background: '#fefce8', borderTop: '1px solid #fde68a', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Wallet size={14} color="#d97706" />
                <span style={{ fontSize: '13px', color: '#92400e', fontWeight: 800 }}>
                  À collecter : {totalToCollect.toLocaleString('fr-FR')} FCFA
                </span>
                <span style={{ fontSize: '10px', color: '#b45309', marginLeft: 'auto', fontWeight: 600 }}>
                  {active.length} livraison{active.length > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Lien vers historique */}
            {historiqueItems.length > 0 && (
              <button
                onClick={() => setShowHistorique(true)}
                style={{ width: '100%', background: 'white', border: 'none', borderTop: '1px solid #f1f5f9', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Voir l'historique ({historiqueItems.length})</span>
                <ChevronRight size={13} color="#94a3b8" />
              </button>
            )}
          </div>

          {/* ── Tabs ── */}
          <div style={{ background: '#e2e8f0', borderRadius: '13px', padding: '3px', display: 'flex', gap: '3px', marginBottom: '12px' }}>
            {[
              { id: 'active',  label: 'En cours', count: active.length,          color: '#173b78' },
              { id: 'livrees', label: 'Livrées',  count: todayDelivered.length,  color: '#15803d' },
              { id: 'retours', label: 'Retours',  count: todayRetours.length,    color: '#be123c' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ flex: 1, padding: '9px 8px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', transition: 'all 0.15s', background: activeTab === tab.id ? 'white' : 'transparent', color: activeTab === tab.id ? tab.color : '#64748b', boxShadow: activeTab === tab.id ? '0 1px 4px rgba(15,23,42,0.1)' : 'none' }}>
                {tab.label}
                {tab.count > 0 && (
                  <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '20px', background: activeTab === tab.id ? tab.color : '#cbd5e1', color: 'white', fontWeight: 800 }}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* ── Liste livraisons ── */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[0,1,2].map(i => <SkeletonRow key={i} />)}
            </div>

          ) : activeTab === 'livrees' ? (
            /* ── Onglet Livrées aujourd'hui : liste plate ── */
            todayDelivered.length === 0 ? (
              <div style={{ background: 'white', borderRadius: '16px', padding: '48px 24px', textAlign: 'center', border: '1px solid #eef0f6' }}>
                <Package size={24} color="#cbd5e1" style={{ margin: '0 auto 14px' }} />
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#374151' }}>Aucune livraison livrée</div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>Tes livraisons livrées apparaîtront ici</div>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2f5ea', overflow: 'hidden', boxShadow: '0 2px 8px rgba(10,40,20,0.04)' }}>
                <div style={{ padding: '4px 8px 8px' }}>
                  {todayDelivered.map((d, i) => (
                    <div key={d.id}
                      onClick={() => setSelectedDelivery(d)}
                      role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') setSelectedDelivery(d); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '10px', cursor: 'pointer', background: i % 2 === 0 ? 'white' : '#fafbfd', borderBottom: i < todayDelivered.length - 1 ? '1px solid #f0fdf4' : 'none' }}>
                      <div style={{ width: '3px', alignSelf: 'stretch', borderRadius: '2px', background: orderBarColor(d), flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.client || 'Client'}</div>
                        {d.product_name && <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.product_name}{d.quantity > 1 ? ` ×${d.quantity}` : ''}</div>}
                        {d.address && <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><MapPin size={9} />{d.address}</div>}
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        {d.amount && <div style={{ fontSize: '13px', fontWeight: 800, color: '#15803d' }}>{Number(d.amount).toLocaleString('fr-FR')} F</div>}
                        {d.order_number && <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>#{d.order_number}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )

          ) : activeList.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '16px', padding: '48px 24px', textAlign: 'center', border: '1px solid #eef0f6' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Package size={24} color="#cbd5e1" />
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                {activeTab === 'active' ? 'Aucune livraison en cours' : 'Aucun retour'}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: activeTab === 'active' && !isOnDuty ? '16px' : 0 }}>
                {activeTab === 'active' ? 'Les nouvelles livraisons apparaîtront ici' : 'Les retours apparaîtront ici'}
              </div>
              {activeTab === 'active' && !isOnDuty && (
                <button onClick={startDuty}
                  style={{ padding: '11px 20px', borderRadius: '11px', border: 'none', background: 'linear-gradient(135deg, #22c55e, #15803d)', color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(34,197,94,0.3)' }}>
                  Démarrer le service
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {activeList.map(d => {
                const s = statusConfig[d.status] || statusConfig.pending;
                const Icon = s.icon;
                const mapsUrl = d.address ? `https://maps.google.com/?q=${encodeURIComponent(d.address)}` : null;
                const isActive = ['pending', 'transit'].includes(d.status);
                return (
                  <div key={d.id}
                    style={{ background: 'white', borderRadius: '14px', border: `1.5px solid ${d.status === 'transit' ? '#bfdbfe' : '#eef0f6'}`, boxShadow: d.status === 'transit' ? '0 4px 18px rgba(59,130,246,0.12)' : '0 2px 10px rgba(10,22,40,0.05)', overflow: 'hidden' }}>

                    <div onClick={() => setSelectedDelivery(d)} role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDelivery(d); } }}
                      style={{ display: 'flex', cursor: 'pointer', padding: '13px 14px', gap: '10px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ width: '3px', alignSelf: 'stretch', borderRadius: '3px', background: orderBarColor(d), flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}`, borderRadius: '6px', padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                            <Icon size={9} /> {s.label}
                          </span>
                          {d.order_number && (
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#cbd5e1', fontFamily: 'monospace' }}>#{d.order_number}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.client || 'Client'}
                        </div>
                        {d.product_name && (
                          <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.product_name}{d.quantity > 1 ? ` ×${d.quantity}` : ''}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin size={11} color="#94a3b8" style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.address}
                          </span>
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', minWidth: '68px' }}>
                        {d.amount && (
                          <div style={{ background: isActive ? 'linear-gradient(135deg, #07162d, #112c52)' : '#f8fafc', borderRadius: '10px', padding: '6px 9px', textAlign: 'right', border: isActive ? 'none' : '1px solid #e2e8f0', maxWidth: '100px', overflow: 'hidden' }}>
                            <div style={{ fontSize: '14px', fontWeight: 800, color: isActive ? 'white' : '#475569', lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {Number(d.amount).toLocaleString('fr-FR')}
                            </div>
                            <div style={{ fontSize: '9px', color: isActive ? 'rgba(255,255,255,0.4)' : '#94a3b8', fontWeight: 600, marginTop: '1px' }}>FCFA</div>
                          </div>
                        )}
                        <ChevronRight size={14} color="#cbd5e1" />
                      </div>
                    </div>

                    {mapsUrl && isActive && (
                      <div style={{ borderTop: `1px solid ${d.status === 'transit' ? '#dbeafe' : '#f1f5f9'}`, background: d.status === 'transit' ? '#f0f7ff' : '#fafbfd', padding: '8px 14px' }}>
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: d.status === 'transit' ? '#1d4ed8' : '#64748b', textDecoration: 'none' }}>
                          <Navigation size={11} /> Ouvrir dans Maps
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Vue Historique (overlay plein écran) ── */}
      {showHistorique && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f0f3f8', overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', position: 'sticky', top: 0, zIndex: 1 }}>
            <button onClick={() => setShowHistorique(false)}
              style={{ background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: '9px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
              <X size={16} />
            </button>
            <div>
              <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '15px', fontWeight: 800, color: 'white' }}>Mon historique</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{historiqueItems.length} livraison{historiqueItems.length > 1 ? 's' : ''} au total</div>
            </div>
          </div>

          <div style={{ padding: '12px' }}>
            {historiqueByDay.length === 0 ? (
              <div style={{ background: 'white', borderRadius: '16px', padding: '48px 24px', textAlign: 'center', border: '1px solid #eef0f6', marginTop: '8px' }}>
                <div style={{ fontSize: '14px', color: '#94a3b8' }}>Aucun historique disponible</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {historiqueByDay.map(group => {
                  const groupLivrees  = group.items.filter(d => d.status === 'delivered');
                  const groupRetours  = group.items.filter(d => d.status === 'failed');
                  const groupAmount   = groupLivrees.reduce((s, d) => s + (Number(d.amount) || 0), 0);
                  return (
                    <div key={group.key} style={{ background: 'white', borderRadius: '16px', border: '1px solid #eef0f6', overflow: 'hidden', boxShadow: '0 2px 8px rgba(10,22,40,0.04)' }}>
                      {/* Header jour */}
                      <div style={{ padding: '11px 16px', background: '#f8fafc', borderBottom: '1px solid #eef0f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{group.label}</div>
                          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                            {groupLivrees.length} livrée{groupLivrees.length > 1 ? 's' : ''}
                            {groupRetours.length > 0 ? ` · ${groupRetours.length} retour${groupRetours.length > 1 ? 's' : ''}` : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#15803d' }}>{groupAmount.toLocaleString('fr-FR')} F</div>
                      </div>
                      {/* Lignes */}
                      <div style={{ padding: '4px 8px 8px' }}>
                        {group.items.map((d, i) => {
                          const s = statusConfig[d.status] || statusConfig.pending;
                          const SIcon = s.icon;
                          return (
                            <div key={d.id}
                              onClick={() => setSelectedDelivery(d)}
                              role="button" tabIndex={0}
                              onKeyDown={e => { if (e.key === 'Enter') setSelectedDelivery(d); }}
                              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '10px', cursor: 'pointer', background: i % 2 === 0 ? 'white' : '#fafbfd', borderBottom: i < group.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                              <div style={{ width: '3px', alignSelf: 'stretch', borderRadius: '2px', background: orderBarColor(d), flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.client || 'Client'}</div>
                                {d.product_name && <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.product_name}</div>}
                                <span style={{ fontSize: '9px', fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}`, borderRadius: '4px', padding: '1px 5px', display: 'inline-flex', alignItems: 'center', gap: '2px', marginTop: '2px' }}>
                                  <SIcon size={8} /> {s.label}
                                </span>
                              </div>
                              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                {d.amount && <div style={{ fontSize: '13px', fontWeight: 800, color: d.status === 'delivered' ? '#15803d' : '#64748b' }}>{Number(d.amount).toLocaleString('fr-FR')} F</div>}
                                {d.order_number && <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>#{d.order_number}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Panneau Règlement ── */}
      {showReglement && <Reglement role="driver" onClose={() => setShowReglement(false)} />}

      {/* ── Panneau Mon Profil ── */}
      {showProfil && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f0f3f8', overflowY: 'auto' }}>
          <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', position: 'sticky', top: 0, zIndex: 1 }}>
            <button onClick={() => setShowProfil(false)}
              style={{ background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: '9px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
              <X size={16} />
            </button>
            <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '15px', fontWeight: 800, color: 'white' }}>Mon profil</div>
          </div>

          <div style={{ padding: '16px' }}>
            {/* Infos */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '16px', border: '1.5px solid #eef0f6', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontWeight: 800, fontSize: '18px', flexShrink: 0 }}>
                  {driver?.initials}
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>{driver?.name}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>Livreur · Wakaana Express</div>
                </div>
              </div>
            </div>

            {/* Changer PIN */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '16px', border: '1.5px solid #eef0f6' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Changer mon code PIN</div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px' }}>Minimum 4 caractères</div>

              {pinSuccess && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '10px 13px', marginBottom: '12px', fontSize: '13px', color: '#15803d', fontWeight: 600 }}>
                  ✓ Code PIN mis à jour avec succès
                </div>
              )}

              {pinError && (
                <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px', padding: '10px 13px', marginBottom: '12px', fontSize: '13px', color: '#be123c', fontWeight: 600 }}>
                  {pinError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                {[
                  { label: 'Code PIN actuel', value: pinCurrent, onChange: setPinCurrent, placeholder: 'Votre code actuel' },
                  { label: 'Nouveau code PIN', value: pinNew, onChange: setPinNew, placeholder: 'Nouveau code (min. 4 car.)' },
                  { label: 'Confirmer le nouveau PIN', value: pinConfirm, onChange: setPinConfirm, placeholder: 'Répéter le nouveau code' },
                ].map((f, i) => (
                  <div key={i}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '5px' }}>{f.label}</label>
                    <input type="password" value={f.value} onChange={e => { f.onChange(e.target.value); setPinError(''); }}
                      placeholder={f.placeholder}
                      style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', color: '#0f172a', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>

              <button onClick={handleChangePin}
                disabled={pinSaving || !pinCurrent || pinNew.length < 4 || !pinConfirm}
                style={{ width: '100%', padding: '13px', borderRadius: '11px', border: 'none', background: pinSaving || !pinCurrent || pinNew.length < 4 || !pinConfirm ? '#e2e8f0' : 'linear-gradient(135deg, #173b78, #2a66b8)', color: pinSaving || !pinCurrent || pinNew.length < 4 || !pinConfirm ? '#94a3b8' : 'white', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                {pinSaving ? 'Enregistrement...' : 'Changer le PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Panneau Ma Présence ── */}
      {showPresences && (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const todayStr = now.toISOString().split('T')[0];
        const todayLog = attendanceLogs.find(l => l.date === todayStr);
        const totalLate = attendanceLogs.reduce((s, l) => s + (l.late_minutes || 0), 0);
        const confirmedLate = attendanceLogs
          .filter(l => l.is_late && l.deduction_status === 'confirmed')
          .reduce((s, l) => s + (l.late_minutes || 0), 0);
        const lateCount = attendanceLogs.filter(l => l.is_late).length;
        const daysWorked = attendanceLogs.filter(l => l.started_at).length;
        const workDays = workingDaysInMonth(year, month);
        const salary = driver?.monthly_salary || 0;
        const deduction = salary > 0
          ? Math.round((confirmedLate / WORK_MINUTES) * (salary / workDays))
          : null;

        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f0f3f8', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', position: 'sticky', top: 0, zIndex: 1 }}>
              <button onClick={() => setShowPresences(false)}
                style={{ background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: '9px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
                <X size={16} />
              </button>
              <div>
                <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '15px', fontWeight: 800, color: 'white' }}>Ma présence</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                  {now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px' }}>

              {/* Aujourd'hui */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', border: '1.5px solid #eef0f6', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Aujourd'hui</div>
                {attendanceLoading ? (
                  <div style={{ height: '48px', background: '#f1f5f9', borderRadius: '10px' }} />
                ) : todayLog?.started_at ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: todayLog.is_late ? '#fff7ed' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${todayLog.is_late ? '#fed7aa' : '#bbf7d0'}` }}>
                        {todayLog.is_late
                          ? <AlertTriangle size={16} color="#c2410c" />
                          : <CheckCircle size={16} color="#15803d" />}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                          {todayLog.is_late ? `En retard de ${todayLog.late_minutes} min` : 'À l\'heure ✓'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          Début : {fmtTime(todayLog.started_at)}
                          {todayLog.ended_at ? ` · Fin : ${fmtTime(todayLog.ended_at)}` : ' · En service'}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '16px' }}>
                    Journée pas encore démarrée
                  </div>
                )}
              </div>

              {/* Récap du mois */}
              <div className="stat-tiles stat-tiles--2" style={{ marginBottom: '12px' }}>
                {[
                  { label: 'Jours travaillés', value: daysWorked, accent: '#16a34a' },
                  { label: 'Jours en retard',  value: lateCount,  accent: '#c2410c' },
                  { label: 'Retard cumulé',    value: `${totalLate} min`, accent: '#173b78', money: true },
                  { label: 'Déduction estim.', value: deduction !== null ? `${deduction.toLocaleString('fr-FR')} F` : 'N/A', accent: '#c53a2d', money: true },
                ].map((k) => (
                  <div key={k.label} className="stat-tile stat-tile--static">
                    <span className={`stat-tile-bar${k.alert ? ' stat-tile-bar--alert' : ''}`} />
                    <span className="stat-tile-text">
                      <span className={`stat-tile-value${k.money ? ' stat-tile-value--money' : ''}`} style={{ color: k.accent }}>
                        {k.value}
                      </span>
                      <span className="stat-tile-label">{k.label}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Historique par jour */}
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Historique du mois
              </div>
              {attendanceLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[0,1,2].map(i => <div key={i} style={{ height: '52px', background: 'white', borderRadius: '10px', opacity: 0.5 }} />)}
                </div>
              ) : attendanceLogs.length === 0 ? (
                <div style={{ background: 'white', borderRadius: '14px', padding: '32px', textAlign: 'center', border: '1px solid #eef0f6' }}>
                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>Aucun pointage ce mois</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {attendanceLogs.map(log => (
                    <div key={log.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      background: 'white', borderRadius: '12px', padding: '12px 14px',
                      border: `1.5px solid ${log.is_late ? '#fed7aa' : log.absent ? '#fecdd3' : '#eef0f6'}`,
                    }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: log.absent ? '#fff1f2' : log.is_late ? '#fff7ed' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {log.absent
                          ? <XCircle size={15} color="#be123c" />
                          : log.is_late
                            ? <AlertTriangle size={15} color="#c2410c" />
                            : <CheckCircle size={15} color="#15803d" />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{fmtDate(log.date)}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>
                          {log.absent ? 'Absent' : log.started_at
                            ? `${fmtTime(log.started_at)} → ${log.ended_at ? fmtTime(log.ended_at) : 'en cours'}`
                            : '—'}
                        </div>
                      </div>
                      {log.is_late && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#c2410c', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '3px 8px', whiteSpace: 'nowrap' }}>
                            +{log.late_minutes} min
                          </span>
                          {log.deduction_status === 'confirmed' && (
                            <span style={{ fontSize: '9px', fontWeight: 700, color: '#be123c', background: '#fff1f2', borderRadius: '6px', padding: '1px 6px' }}>Confirmé</span>
                          )}
                          {log.deduction_status === 'excused' && (
                            <span style={{ fontSize: '9px', fontWeight: 700, color: '#15803d', background: '#f0fdf4', borderRadius: '6px', padding: '1px 6px' }}>Excusé ✓</span>
                          )}
                          {log.deduction_status === 'pending' && (
                            <span style={{ fontSize: '9px', fontWeight: 600, color: '#92400e', background: '#fef3c7', borderRadius: '6px', padding: '1px 6px' }}>En attente</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Modal motif retour ── */}
      {motifModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.7)', backdropFilter: 'blur(6px)' }}
          onClick={() => setMotifModal(null)}>
          <div style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '680px', margin: '0 auto', padding: '24px 20px', paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))', boxShadow: '0 -24px 80px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e2e8f0', margin: '0 auto 20px' }} />
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Motif du retour</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Explique pourquoi la livraison n'a pas pu être effectuée</div>
            <textarea
              autoFocus
              value={motifText}
              onChange={e => setMotifText(e.target.value)}
              placeholder="Ex: Client absent, mauvaise adresse, client a refusé..."
              rows={3}
              style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', color: '#0f172a' }}
              onFocus={e => { e.target.style.borderColor = '#be123c'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => setMotifModal(null)}
                style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: 'white', fontSize: '14px', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleMotifConfirm}
                style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #f43f5e, #be123c)', fontSize: '14px', fontWeight: 800, color: 'white', cursor: 'pointer', boxShadow: '0 6px 16px rgba(190,18,60,0.3)' }}>
                Confirmer le retour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Détail livraison (bottom sheet) ── */}
      {selectedDelivery && (() => {
        const sd = selectedDelivery;
        const ss = statusConfig[sd.status] || statusConfig.pending;
        const SIcon = ss.icon;
        const sdActions = nextStatus[sd.status] || [];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.65)', backdropFilter: 'blur(6px)' }}
            onClick={() => setSelectedDelivery(null)}>
            <div style={{ background: '#f8fafc', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '680px', margin: '0 auto', maxHeight: '88vh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', boxShadow: '0 -24px 80px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}>

              {/* Handle */}
              <div style={{ padding: '14px 0 0', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e2e8f0' }} />
              </div>

              {/* Titre + statut */}
              <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '20px', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{sd.client || 'Client'}</div>
                  {sd.order_number && <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', marginTop: '2px' }}>#{sd.order_number}</div>}
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '10px', background: ss.bg, border: `1px solid ${ss.border}`, fontSize: '12px', fontWeight: 700, color: ss.color }}>
                  <SIcon size={13} /> {ss.label}
                </span>
              </div>

              <div style={{ padding: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                {/* Montant */}
                {sd.amount && (
                  <div style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #07162d, #173b78)', padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700 }}>Montant à collecter</div>
                    <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '22px', fontWeight: 800, color: 'white' }}>
                      {Number(sd.amount).toLocaleString('fr-FR')} <span style={{ fontSize: '12px', opacity: 0.4 }}>FCFA</span>
                    </div>
                  </div>
                )}

                {/* Appel + WhatsApp */}
                {sd.phone && (() => {
                  const digits = String(sd.phone).replace(/\D/g, '');
                  const waNum = digits.length === 9 ? `221${digits}` : digits.length === 14 && digits.startsWith('00221') ? digits.slice(2) : digits;
                  return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a href={`tel:${sd.phone}`} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '14px', background: 'linear-gradient(135deg, #173b78, #2a66b8)', color: 'white', textDecoration: 'none', boxShadow: '0 6px 18px rgba(23,59,120,0.3)' }}>
                        <Phone size={16} />
                        <span style={{ fontSize: '13px', fontWeight: 800 }}>Appeler</span>
                      </a>
                      <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '14px', background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', textDecoration: 'none', boxShadow: '0 6px 18px rgba(22,163,74,0.3)' }}>
                        <MessageCircle size={16} />
                        <span style={{ fontSize: '13px', fontWeight: 800 }}>WhatsApp</span>
                      </a>
                    </div>
                  );
                })()}

                {/* ── Position GPS du client ──────────────────────────
                    Toujours visible : sans position, le livreur doit pouvoir
                    la demander en un geste plutôt que de deviner. */}
                {(() => {
                  const hasPos = Boolean(sd.client_lat && sd.client_lng);
                  const stale  = hasPos && isStale(sd.client_location_at);
                  const digits = String(sd.phone || '').replace(/\D/g, '');
                  const waNum  = digits.length === 9 ? `221${digits}`
                    : digits.length === 14 && digits.startsWith('00221') ? digits.slice(2)
                    : digits;
                  // Pas de lien : le client envoie sa localisation directement dans WhatsApp.
                  const firstName = String(sd.client || '').trim().split(' ')[0];
                  const askMsg = encodeURIComponent(
                    `Bonjour${firstName ? ` ${firstName}` : ''},\n` +
                    `Je suis votre livreur Wakaana Express` +
                    `${sd.store ? `, en route avec votre commande ${sd.store}` : ''}.\n` +
                    `Pour éviter tout dérangement et vous livrer plus rapidement, ` +
                    `merci de me partager votre localisation ici.\nMerci !`
                  );

                  // Ces liens lancent le guidage, ils ne se contentent pas d'afficher le point
                  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${sd.client_lat},${sd.client_lng}`;
                  const waze  = `https://waze.com/ul?ll=${sd.client_lat},${sd.client_lng}&navigate=yes`;

                  if (!hasPos) {
                    return (
                      <a
                        href={waNum ? `https://wa.me/${waNum}?text=${askMsg}` : undefined}
                        target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', borderRadius: '14px', background: waNum ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#e2e8f0', color: waNum ? 'white' : '#94a3b8', textDecoration: 'none', fontSize: '13px', fontWeight: 800, pointerEvents: waNum ? 'auto' : 'none' }}>
                        <MessageCircle size={15} />
                        {waNum ? 'Demander la position' : 'Aucun numéro'}
                      </a>
                    );
                  }

                  const border = stale ? '#fed7aa' : '#bbf7d0';
                  const tint   = stale ? '#fff7ed' : '#f0fdf4';
                  const ink    = stale ? '#c2410c' : '#15803d';

                  return (
                    <div style={{ background: 'white', borderRadius: '14px', border: `1.5px solid ${border}`, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: tint, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <MapPin size={14} color={ink} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: ink }}>
                            Position du client
                          </div>
                          <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                            {timeAgo(sd.client_location_at) || 'partagée par le client'}
                            {sd.client_location_accuracy ? ` · ± ${Math.round(sd.client_location_accuracy)} m` : ''}
                            {stale ? ' · peut être périmée' : ''}
                          </div>
                        </div>
                      </div>

                      <div style={{ padding: '0 12px 12px', display: 'flex', gap: '8px' }}>
                        <a href={gmaps} target="_blank" rel="noopener noreferrer"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '11px', borderRadius: '10px', background: 'linear-gradient(135deg,#1a73e8,#1557b0)', color: 'white', textDecoration: 'none', fontSize: '12px', fontWeight: 800 }}>
                          <Navigation size={13} /> Google Maps
                        </a>
                        <a href={waze} target="_blank" rel="noopener noreferrer"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '11px', borderRadius: '10px', background: 'linear-gradient(135deg,#33ccff,#1eb8e8)', color: '#06283d', textDecoration: 'none', fontSize: '12px', fontWeight: 800 }}>
                          <Navigation size={13} /> Waze
                        </a>
                      </div>
                    </div>
                  );
                })()}

                {/* Adresse */}
                {sd.address && (
                  <div style={{ background: 'white', borderRadius: '14px', padding: '14px 16px', border: '1px solid #eef0f6', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <MapPin size={16} color="#c2410c" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: '3px' }}>Adresse</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', lineHeight: 1.4, marginBottom: '8px' }}>{sd.address}</div>
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(sd.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', background: 'linear-gradient(135deg, #1a73e8, #1557b0)', color: 'white', fontSize: '12px', fontWeight: 700, textDecoration: 'none' }}
                      >
                        <Navigation size={12} /> Naviguer
                      </a>
                    </div>
                  </div>
                )}

                {/* Infos commande */}
                {(sd.product_name || sd.store) && (
                  <div style={{ background: 'white', borderRadius: '14px', padding: '14px 16px', border: '1px solid #eef0f6', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {sd.product_name && (
                      <div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: '3px' }}>Produit</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                          {sd.product_name}{sd.quantity > 1 ? <span style={{ color: '#64748b', fontWeight: 500 }}> × {sd.quantity}</span> : ''}
                        </div>
                      </div>
                    )}
                    {sd.store && (
                      <div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700, marginBottom: '3px' }}>Boutique</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#7c3aed' }}>{sd.store}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                {sdActions.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                    {sdActions.map(action => (
                      <button key={action.value}
                        onClick={() => handleActionClick(sd.id, action)}
                        style={{ flex: '1 1 160px', padding: '15px', borderRadius: '14px', border: 'none', background: action.bg, color: 'white', fontSize: '14px', fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,0.18)', WebkitTapHighlightColor: 'transparent' }}>
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
